import type { BackgroundDefinition } from '@/types';

/** Background roster: the 3 original generic themes (used as song defaults today)
 * plus holiday and seasonal themes the player can pick as a global override. Each
 * background's illustrated backdrop lives at public/images/backgrounds/<id>.jpg
 * (see PreloadScene/Background.ts); this file only carries the cosmetic metadata
 * (name, unlock price, and the small floating motif icon drawn on top of the
 * backdrop). Add a new entry + matching image to make a background selectable
 * everywhere with no other code changes. */
export const BACKGROUNDS: BackgroundDefinition[] = [
  { id: 'sunsetHill', name: 'Sunset Hill', category: 'default', motif: 'none', motifColor: 0xffffff, unlockedByDefault: true, price: 0 },
  { id: 'candyNight', name: 'Candy Night', category: 'default', motif: 'none', motifColor: 0xffffff, unlockedByDefault: true, price: 0 },
  { id: 'oceanBreeze', name: 'Ocean Breeze', category: 'default', motif: 'none', motifColor: 0xffffff, unlockedByDefault: true, price: 0 },
  {
    id: 'valentines',
    name: "Valentine's Day",
    category: 'holiday',
    motif: 'heart',
    motifColor: 0xff5c8a,
    motifColor2: 0xffffff,
    unlockedByDefault: false,
    price: 150,
  },
  {
    id: 'stPatricks',
    name: "St. Patrick's Day",
    category: 'holiday',
    motif: 'shamrock',
    motifColor: 0x2fa84f,
    unlockedByDefault: false,
    price: 150,
  },
  {
    id: 'easter',
    name: 'Easter',
    category: 'holiday',
    motif: 'egg',
    motifColor: 0xffb3d9,
    motifColor2: 0xb3e0ff,
    unlockedByDefault: false,
    price: 150,
  },
  {
    id: 'independenceDay',
    name: 'Independence Day',
    category: 'holiday',
    motif: 'star',
    motifColor: 0xffffff,
    motifColor2: 0xff5c5c,
    unlockedByDefault: false,
    price: 200,
  },
  {
    id: 'halloween',
    name: 'Halloween',
    category: 'holiday',
    motif: 'pumpkin',
    motifColor: 0xff8c2b,
    motifColor2: 0x3a7d3a,
    unlockedByDefault: false,
    price: 200,
  },
  {
    id: 'thanksgiving',
    name: 'Thanksgiving',
    category: 'holiday',
    motif: 'leaf',
    motifColor: 0xd9622b,
    motifColor2: 0xffb347,
    unlockedByDefault: false,
    price: 150,
  },
  {
    id: 'christmas',
    name: 'Christmas',
    category: 'holiday',
    motif: 'gift',
    motifColor: 0xd9333f,
    motifColor2: 0x2fa84f,
    unlockedByDefault: false,
    price: 200,
  },
  {
    id: 'newYears',
    name: "New Year's",
    category: 'holiday',
    motif: 'firework',
    motifColor: 0xffe066,
    motifColor2: 0xff8fb1,
    unlockedByDefault: false,
    price: 200,
  },
  { id: 'summer', name: 'Summer', category: 'season', motif: 'sun', motifColor: 0xffd23f, unlockedByDefault: true, price: 0 },
  { id: 'winter', name: 'Winter', category: 'season', motif: 'snowflake', motifColor: 0xffffff, unlockedByDefault: true, price: 0 },
  {
    id: 'spring',
    name: 'Spring',
    category: 'season',
    motif: 'flower',
    motifColor: 0xff96c8,
    motifColor2: 0xffe066,
    unlockedByDefault: true,
    price: 0,
  },
  {
    id: 'fall',
    name: 'Fall',
    category: 'season',
    motif: 'leaf',
    motifColor: 0xd9622b,
    motifColor2: 0xffb347,
    unlockedByDefault: true,
    price: 0,
  },
];

export function getBackgroundById(id: string): BackgroundDefinition {
  const bg = BACKGROUNDS.find((b) => b.id === id);
  if (!bg) throw new Error(`Unknown background id: ${id}`);
  return bg;
}

export function backgroundTextureKey(id: string): string {
  return `bg-${id}`;
}
